import styles from './DynamicFormBuilder.module.css';

const OPERATORS = ['present', 'equals', 'not_equals', 'in', 'not_in'];

const INPUT_CLASS = 'form-control form-control-sm';
const SELECT_CLASS = 'form-select form-select-sm';

// Walks outward from the field's parent group up to the root, collecting
// candidate source field keys at each level (mirrors Alpaca's existing
// dependency-lookup behaviour described on DynamicForms::Field).
export function eligibleSourceKeys(definition, fieldPath) {
  if (!fieldPath || fieldPath.length === 0) return [];
  const parentPath = fieldPath.slice(0, -1);
  const fieldIdx = fieldPath[fieldPath.length - 1];

  const fields = Array.isArray(definition?.fields) ? definition.fields : [];
  const seen = new Set();
  const keys = [];

  const addLevel = (siblings, excludeIdx) => {
    siblings.forEach((sib, i) => {
      if (i === excludeIdx) return;
      const k = sib?.key;
      if (k && !seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    });
  };

  let cursor = fields;
  for (let i = 0; i < parentPath.length; i++) {
    cursor = cursor[parentPath[i]];
    cursor = Array.isArray(cursor?.children) ? cursor.children : [];
  }
  addLevel(cursor, fieldIdx);

  for (let level = parentPath.length - 1; level >= 0; level--) {
    let ancestorList = fields;
    for (let i = 0; i < level; i++) {
      ancestorList = ancestorList[parentPath[i]];
      ancestorList = Array.isArray(ancestorList?.children) ? ancestorList.children : [];
    }
    addLevel(ancestorList, parentPath[level]);
  }

  return keys;
}

function renderValueInput(operator, value, onChange) {
  if (operator === 'present') return null;
  if (operator === 'in' || operator === 'not_in') {
    const csv = Array.isArray(value) ? value.join(',') : '';
    return (
      <input
        type="text"
        className={INPUT_CLASS}
        placeholder="comma-separated values"
        value={csv}
        onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter((s) => s.length > 0))}
      />
    );
  }
  return (
    <input
      type="text"
      className={INPUT_CLASS}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function VisibilityEditor({ rules, candidateKeys, onChange }) {
  const list = Array.isArray(rules) ? rules : [];

  const replace = (i, next) => {
    const updated = [...list];
    updated[i] = next;
    onChange(updated);
  };

  const removeRow = (i) => {
    const updated = [...list];
    updated.splice(i, 1);
    onChange(updated);
  };

  const move = (i, delta) => {
    const target = i + delta;
    if (target < 0 || target >= list.length) return;
    const updated = [...list];
    [updated[i], updated[target]] = [updated[target], updated[i]];
    onChange(updated);
  };

  const addRow = () => {
    onChange([...list, { source_field_key: candidateKeys[0] || '', operator: 'present' }]);
  };

  const onOperatorChange = (i, nextOp) => {
    const current = list[i] || {};
    const next = { ...current, operator: nextOp };
    if (nextOp === 'present') {
      delete next.value;
    } else if (nextOp === 'in' || nextOp === 'not_in') {
      if (!Array.isArray(next.value)) next.value = [];
    } else if (Array.isArray(next.value)) {
      next.value = next.value[0] ?? '';
    }
    replace(i, next);
  };

  return (
    <div className="d-flex flex-column gap-2 align-items-start">
      {list.length === 0 ? (
        <p className="m-0 small text-muted fst-italic">No visibility rules. Field is always visible.</p>
      ) : (
        <table className="table table-sm small mb-0 w-100">
          <thead>
            <tr>
              <th>Source field</th>
              <th>Operator</th>
              <th>Value</th>
              <th aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {list.map((rule, i) => {
              const isFirst = i === 0;
              const isLast = i === list.length - 1;
              return (
                <tr key={i}>
                  <td>
                    <select
                      className={SELECT_CLASS}
                      value={rule.source_field_key ?? ''}
                      onChange={(e) => replace(i, { ...rule, source_field_key: e.target.value })}
                    >
                      {!candidateKeys.includes(rule.source_field_key) && rule.source_field_key && (
                        <option value={rule.source_field_key}>{rule.source_field_key} (missing)</option>
                      )}
                      {candidateKeys.length === 0 && <option value="">(no candidates)</option>}
                      {candidateKeys.map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className={SELECT_CLASS}
                      value={rule.operator ?? 'present'}
                      onChange={(e) => onOperatorChange(i, e.target.value)}
                    >
                      {OPERATORS.map((op) => (
                        <option key={op} value={op}>{op}</option>
                      ))}
                    </select>
                  </td>
                  <td>{renderValueInput(rule.operator, rule.value, (v) => replace(i, { ...rule, value: v }))}</td>
                  <td>
                    <div className="d-flex gap-1 text-nowrap">
                      <button type="button" className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`} title="Move up" disabled={isFirst} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`} title="Move down" disabled={isLast} onClick={() => move(i, 1)}>↓</button>
                      <button type="button" className={`btn btn-sm btn-outline-danger ${styles.iconButton}`} title="Remove rule" onClick={() => removeRow(i)}>×</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addRow}>+ Add rule</button>
    </div>
  );
}
